import React from "react";
import type { AtlasLanguage } from "../../translations";
import type { EvidenceItemV2, SurgicalPlanV2, SurgicalTechniqueV2 } from "../../utils/surgicalPlanV2";

interface Props {
  plan: SurgicalPlanV2;
  language: AtlasLanguage;
  patientName?: string;
  doctorName?: string;
  doctorCrm?: string;
  generatedAt?: string;
}

const copy = {
  pt: {
    title: "Plano Cirúrgico",
    subtitle: "Planejamento Clínico e Estratégia Operatória",
    generatedBy: "Gerado por OrthoPrecision",
    patient: "Paciente",
    doctor: "Médico responsável",
    date: "Data de geração",
    diagnosis: "Diagnóstico",
    location: "Localização anatômica",
    ao: "Classificação AO/OTA",
    decisionTitle: "Decisão de Conduta",
    decisionSurgical: "CIRÚRGICO",
    decisionConservative: "CONSERVADOR",
    decisionIndeterminate: "INDETERMINADO",
    rationale: "Justificativa",
    surgicalCriteria: "Critérios cirúrgicos",
    conservativeCriteria: "Critérios conservadores",
    conservativeTitle: "Tratamento Conservador",
    indication: "Indicação",
    immobilization: "Imobilização / Proteção",
    medication: "Medicação",
    rehabilitation: "Reabilitação",
    followUp: "Seguimento",
    expectedRecovery: "Recuperação esperada",
    failureCriteria: "Critérios de falha",
    conversionSurgery: "Conversão para cirurgia",
    technique: "Técnica Cirúrgica",
    when: "Quando escolher",
    positioning: "Posicionamento",
    approach: "Via de acesso",
    steps: "Técnica passo a passo",
    materials: "Implantes e materiais",
    advantages: "Vantagens",
    risks: "Limitações / riscos",
    complications: "Complicações possíveis",
    healing: "Tempo de consolidação",
    planB: "Plano B intraoperatório",
    postopTitle: "Cuidados Pós-operatórios",
    rangeOfMotion: "Amplitude de movimento",
    physiotherapy: "Fisioterapia",
    weightBearing: "Carga",
    followUpImaging: "Imagem de controle",
    returnToFunction: "Retorno funcional",
    redFlags: "Sinais de alerta",
    differentialsTitle: "Diagnósticos Diferenciais e Lesões Associadas",
    evidenceTitle: "Evidências Científicas",
    relevance: "Relevância",
    contingencyTitle: "Plano de Contingência Intraoperatório",
    disclaimer: "Este documento foi gerado com apoio de inteligência artificial e deve ser utilizado como referência clínica. A decisão final é de responsabilidade do médico assistente.",
    pageOf: "Página",
  },
  en: {
    title: "Surgical Plan",
    subtitle: "Clinical Planning and Operative Strategy",
    generatedBy: "Generated by OrthoPrecision",
    patient: "Patient",
    doctor: "Attending physician",
    date: "Generated on",
    diagnosis: "Diagnosis",
    location: "Anatomical location",
    ao: "AO/OTA Classification",
    decisionTitle: "Management Decision",
    decisionSurgical: "SURGICAL",
    decisionConservative: "CONSERVATIVE",
    decisionIndeterminate: "INDETERMINATE",
    rationale: "Rationale",
    surgicalCriteria: "Surgical criteria",
    conservativeCriteria: "Conservative criteria",
    conservativeTitle: "Conservative Treatment",
    indication: "Indication",
    immobilization: "Immobilization / Protection",
    medication: "Medication",
    rehabilitation: "Rehabilitation",
    followUp: "Follow-up",
    expectedRecovery: "Expected recovery",
    failureCriteria: "Failure criteria",
    conversionSurgery: "Conversion to surgery",
    technique: "Surgical Technique",
    when: "When to choose",
    positioning: "Positioning",
    approach: "Surgical approach",
    steps: "Step-by-step technique",
    materials: "Implants and materials",
    advantages: "Advantages",
    risks: "Limitations / risks",
    complications: "Possible complications",
    healing: "Consolidation time",
    planB: "Intraoperative Plan B",
    postopTitle: "Postoperative Care",
    rangeOfMotion: "Range of motion",
    physiotherapy: "Physiotherapy",
    weightBearing: "Weight bearing",
    followUpImaging: "Follow-up imaging",
    returnToFunction: "Return to function",
    redFlags: "Red flags",
    differentialsTitle: "Differential Diagnoses and Associated Injuries",
    evidenceTitle: "Scientific Evidence",
    relevance: "Relevance",
    contingencyTitle: "Intraoperative Contingency Plan",
    disclaimer: "This document was generated with AI assistance and should be used as clinical reference. Final decision is the responsibility of the attending physician.",
    pageOf: "Page",
  },
  es: {
    title: "Plan Quirúrgico",
    subtitle: "Planificación Clínica y Estrategia Operatoria",
    generatedBy: "Generado por OrthoPrecision",
    patient: "Paciente",
    doctor: "Médico responsable",
    date: "Fecha de generación",
    diagnosis: "Diagnóstico",
    location: "Localización anatómica",
    ao: "Clasificación AO/OTA",
    decisionTitle: "Decisión de Conducta",
    decisionSurgical: "QUIRÚRGICO",
    decisionConservative: "CONSERVADOR",
    decisionIndeterminate: "INDETERMINADO",
    rationale: "Justificación",
    surgicalCriteria: "Criterios quirúrgicos",
    conservativeCriteria: "Criterios conservadores",
    conservativeTitle: "Tratamiento Conservador",
    indication: "Indicación",
    immobilization: "Inmovilización / Protección",
    medication: "Medicación",
    rehabilitation: "Rehabilitación",
    followUp: "Seguimiento",
    expectedRecovery: "Recuperación esperada",
    failureCriteria: "Criterios de fallo",
    conversionSurgery: "Conversión a cirugía",
    technique: "Técnica Quirúrgica",
    when: "Cuándo elegir",
    positioning: "Posicionamiento",
    approach: "Vía de abordaje",
    steps: "Técnica paso a paso",
    materials: "Implantes y materiales",
    advantages: "Ventajas",
    risks: "Limitaciones / riesgos",
    complications: "Complicaciones posibles",
    healing: "Tiempo de consolidación",
    planB: "Plan B intraoperatorio",
    postopTitle: "Cuidados Postoperatorios",
    rangeOfMotion: "Amplitud de movimiento",
    physiotherapy: "Fisioterapia",
    weightBearing: "Carga",
    followUpImaging: "Imagen de control",
    returnToFunction: "Retorno funcional",
    redFlags: "Señales de alerta",
    differentialsTitle: "Diagnósticos Diferenciales y Lesiones Asociadas",
    evidenceTitle: "Evidencias Científicas",
    relevance: "Relevancia",
    contingencyTitle: "Plan de Contingencia Intraoperatoria",
    disclaimer: "Este documento fue generado con apoyo de inteligencia artificial y debe usarse como referencia clínica. La decisión final es responsabilidad del médico tratante.",
    pageOf: "Página",
  },
};

function l(language: AtlasLanguage) {
  return copy[language === "es" ? "es" : language === "en" ? "en" : "pt"];
}

function SectionBlock({ title, items }: { title: string; items: string[] }) {
  if (!items?.length) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.18em", color: "#0369a1", marginBottom: 6 }}>
        {title}
      </p>
      <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
        {items.map((item, i) => (
          <li key={i} style={{ display: "flex", gap: 8, marginBottom: 4, fontSize: 12, lineHeight: 1.55, color: "#1e293b" }}>
            <span style={{ marginTop: 5, width: 5, height: 5, borderRadius: "50%", backgroundColor: "#0284c7", flexShrink: 0, display: "inline-block" }} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TwoCol({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
      {children}
    </div>
  );
}

function SectionDivider({ title }: { title: string }) {
  return (
    <div style={{ borderTop: "2px solid #0284c7", marginTop: 28, marginBottom: 16, paddingTop: 10 }}>
      <h2 style={{ fontSize: 13, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.2em", color: "#0c4a6e", margin: 0 }}>
        {title}
      </h2>
    </div>
  );
}

function TechniqueSection({ technique, index, labels }: { technique: SurgicalTechniqueV2; index: number; labels: ReturnType<typeof l> }) {
  return (
    <div style={{ pageBreakInside: "avoid", marginBottom: 24 }}>
      <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
        <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.18em", color: "#0369a1", margin: 0 }}>
          {labels.technique} {index + 1}
        </p>
        <h3 style={{ fontSize: 16, fontWeight: 800, color: "#0c4a6e", margin: "4px 0 0" }}>
          {technique.title}
        </h3>
      </div>
      <TwoCol>
        <SectionBlock title={labels.indication} items={technique.indication} />
        <SectionBlock title={labels.when} items={technique.whenToChoose} />
      </TwoCol>
      <TwoCol>
        <SectionBlock title={labels.positioning} items={technique.positioning} />
        <SectionBlock title={labels.approach} items={technique.surgicalApproach} />
      </TwoCol>
      <SectionBlock title={labels.steps} items={technique.stepByStepTechnique} />
      <SectionBlock title={labels.materials} items={technique.implantsAndMaterials} />
      <TwoCol>
        <SectionBlock title={labels.advantages} items={technique.advantages} />
        <SectionBlock title={labels.risks} items={technique.limitationsAndRisks} />
      </TwoCol>
      <TwoCol>
        <SectionBlock title={labels.complications} items={technique.possibleComplications} />
        <SectionBlock title={labels.healing} items={technique.consolidationTime} />
      </TwoCol>
      <SectionBlock title={labels.planB} items={technique.planB} />
      {technique.evidence.length > 0 && (
        <EvidenceSection items={technique.evidence} labels={labels} />
      )}
    </div>
  );
}

function EvidenceSection({ items, labels }: { items: EvidenceItemV2[]; labels: ReturnType<typeof l> }) {
  if (!items.length) return null;
  return (
    <div style={{ marginTop: 12 }}>
      <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.18em", color: "#0369a1", marginBottom: 8 }}>
        {labels.evidenceTitle}
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {items.map((ev, i) => (
          <div key={i} style={{ border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px 10px", background: "#f8fafc" }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: "#1e293b", margin: "0 0 4px" }}>{ev.title}</p>
            {(ev.authors || ev.journal || ev.year) && (
              <p style={{ fontSize: 10, color: "#64748b", margin: "0 0 4px" }}>
                {[ev.authors, ev.journal, ev.year].filter(Boolean).join(" • ")}
              </p>
            )}
            {ev.doi && (
              <p style={{ fontSize: 10, color: "#0369a1", margin: "0 0 4px" }}>DOI: {ev.doi}</p>
            )}
            <p style={{ fontSize: 10, color: "#334155", margin: 0 }}>
              <strong>{labels.relevance}:</strong> {ev.relevance}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

export const SurgicalPlanReportTemplate = React.forwardRef<HTMLDivElement, Props>(
  ({ plan, language, patientName, doctorName, doctorCrm, generatedAt }, ref) => {
    const labels = l(language);
    const decisionLabel =
      plan.managementDecision.type === "surgical"
        ? labels.decisionSurgical
        : plan.managementDecision.type === "conservative"
        ? labels.decisionConservative
        : labels.decisionIndeterminate;

    const decisionColor =
      plan.managementDecision.type === "surgical"
        ? "#b91c1c"
        : plan.managementDecision.type === "conservative"
        ? "#15803d"
        : "#b45309";

    const allEvidence = [...plan.globalEvidence, ...plan.surgicalTechniques.flatMap(t => t.evidence)]
      .filter((ev, i, arr) => arr.findIndex(e => e.title === ev.title) === i);

    return (
      <div
        ref={ref}
        style={{
          fontFamily: "'Helvetica Neue', Arial, sans-serif",
          backgroundColor: "#fff",
          color: "#1e293b",
          maxWidth: 800,
          margin: "0 auto",
          padding: "40px 48px",
          fontSize: 12,
          lineHeight: 1.5,
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "3px solid #0284c7", paddingBottom: 16, marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: "#0c4a6e", margin: 0, letterSpacing: "-0.02em" }}>
              OrthoPrecision
            </h1>
            <p style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.2em", color: "#0369a1", margin: "3px 0 0" }}>
              {labels.subtitle}
            </p>
          </div>
          <div style={{ textAlign: "right" }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", margin: 0 }}>{labels.title}</p>
            {(patientName || doctorName || generatedAt) && (
              <div style={{ marginTop: 6, fontSize: 11, color: "#64748b" }}>
                {patientName && <p style={{ margin: "1px 0" }}>{labels.patient}: <strong style={{ color: "#1e293b" }}>{patientName}</strong></p>}
                {doctorName && <p style={{ margin: "1px 0" }}>{labels.doctor}: <strong style={{ color: "#1e293b" }}>Dr(a). {doctorName}{doctorCrm ? ` · CRM ${doctorCrm}` : ""}</strong></p>}
                {generatedAt && <p style={{ margin: "1px 0" }}>{labels.date}: {generatedAt}</p>}
              </div>
            )}
          </div>
        </div>

        {/* Diagnóstico / Localização / AO */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 20, background: "#f0f9ff", borderRadius: 8, padding: 16 }}>
          <div>
            <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.18em", color: "#0369a1", margin: "0 0 4px" }}>{labels.diagnosis}</p>
            <p style={{ fontSize: 14, fontWeight: 800, color: "#0c4a6e", margin: 0 }}>{plan.diagnosis}</p>
          </div>
          <div>
            <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.18em", color: "#0369a1", margin: "0 0 4px" }}>{labels.location}</p>
            <p style={{ fontSize: 14, fontWeight: 800, color: "#0c4a6e", margin: 0 }}>{plan.anatomicalLocation}</p>
          </div>
          <div>
            <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.18em", color: "#0369a1", margin: "0 0 4px" }}>{labels.ao}</p>
            <p style={{ fontSize: 14, fontWeight: 800, color: "#0c4a6e", margin: 0 }}>{plan.aoOtaClassification || "—"}</p>
          </div>
        </div>

        {/* Decisão de conduta */}
        <div style={{ border: `2px solid ${decisionColor}`, borderRadius: 8, padding: "12px 16px", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.18em", color: "#64748b", margin: 0 }}>{labels.decisionTitle}</p>
            <span style={{ fontSize: 12, fontWeight: 900, letterSpacing: "0.1em", color: "#fff", background: decisionColor, borderRadius: 4, padding: "2px 10px" }}>
              {decisionLabel}
            </span>
          </div>
          <p style={{ fontSize: 13, color: "#1e293b", margin: "0 0 10px" }}>{plan.managementDecision.rationale}</p>
          <TwoCol>
            <SectionBlock title={labels.surgicalCriteria} items={plan.managementDecision.surgicalCriteria} />
            <SectionBlock title={labels.conservativeCriteria} items={plan.managementDecision.conservativeCriteria} />
          </TwoCol>
        </div>

        {/* Tratamento Conservador */}
        {(plan.managementDecision.type === "conservative" || plan.managementDecision.type === "indeterminate") && (
          <>
            <SectionDivider title={labels.conservativeTitle} />
            <TwoCol>
              <SectionBlock title={labels.indication} items={plan.conservativeTreatment.indication} />
              <SectionBlock title={labels.immobilization} items={plan.conservativeTreatment.immobilizationOrProtection} />
              <SectionBlock title={labels.medication} items={plan.conservativeTreatment.medication} />
              <SectionBlock title={labels.rehabilitation} items={plan.conservativeTreatment.rehabilitation} />
              <SectionBlock title={labels.followUp} items={plan.conservativeTreatment.followUp} />
              <SectionBlock title={labels.expectedRecovery} items={plan.conservativeTreatment.expectedRecovery} />
              <SectionBlock title={labels.failureCriteria} items={plan.conservativeTreatment.failureCriteria} />
              <SectionBlock title={labels.conversionSurgery} items={plan.conservativeTreatment.conversionToSurgery} />
            </TwoCol>
          </>
        )}

        {/* Técnicas cirúrgicas */}
        {(plan.managementDecision.type === "surgical" || plan.managementDecision.type === "indeterminate") &&
          plan.surgicalTechniques.map((technique, i) => (
            <React.Fragment key={i}>
              <SectionDivider title={`${labels.technique} ${i + 1}`} />
              <TechniqueSection technique={technique} index={i} labels={labels} />
            </React.Fragment>
          ))}

        {/* Pós-operatório */}
        <SectionDivider title={labels.postopTitle} />
        <TwoCol>
          <SectionBlock title={labels.immobilization} items={plan.postoperativeCare.immobilization} />
          <SectionBlock title={labels.rangeOfMotion} items={plan.postoperativeCare.rangeOfMotion} />
          <SectionBlock title={labels.physiotherapy} items={plan.postoperativeCare.physiotherapy} />
          <SectionBlock title={labels.weightBearing} items={plan.postoperativeCare.weightBearing} />
          <SectionBlock title={labels.followUpImaging} items={plan.postoperativeCare.followUpImaging} />
          <SectionBlock title={labels.returnToFunction} items={plan.postoperativeCare.returnToFunction} />
        </TwoCol>
        <SectionBlock title={labels.redFlags} items={plan.postoperativeCare.redFlags} />

        {/* Plano de contingência */}
        {plan.intraoperativeContingencyPlan.length > 0 && (
          <>
            <SectionDivider title={labels.contingencyTitle} />
            <SectionBlock title="" items={plan.intraoperativeContingencyPlan} />
          </>
        )}

        {/* Diagnósticos diferenciais */}
        {plan.differentialDiagnosisAndAssociatedInjuries.length > 0 && (
          <>
            <SectionDivider title={labels.differentialsTitle} />
            <SectionBlock title="" items={plan.differentialDiagnosisAndAssociatedInjuries} />
          </>
        )}

        {/* Evidências globais */}
        {allEvidence.length > 0 && (
          <>
            <SectionDivider title={labels.evidenceTitle} />
            <EvidenceSection items={allEvidence} labels={labels} />
          </>
        )}

        {/* Footer */}
        <div style={{ marginTop: 40, paddingTop: 16, borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <p style={{ fontSize: 10, color: "#94a3b8", maxWidth: 540, margin: 0 }}>{labels.disclaimer}</p>
          <p style={{ fontSize: 10, color: "#94a3b8", margin: 0, flexShrink: 0 }}>{labels.generatedBy}</p>
        </div>
      </div>
    );
  }
);

SurgicalPlanReportTemplate.displayName = "SurgicalPlanReportTemplate";
